import axios, {AxiosInstance, AxiosResponse, AxiosError} from 'axios';
import {toast} from 'react-toastify';
import settings from '@/settings';
import {logError, logWarn} from '@/shared/utils/logger';
import {exitImpersonationDueToExpiry} from '@/shared/utils/exitImpersonation';

/**
 * Custom API Error class for better error handling
 */
export class CustomApiError extends Error {
    public status?: number;
    public code?: string;
    public data?: any;

    constructor(message: string, status?: number, code?: string, data?: any) {
        super(message);
        this.name = 'CustomApiError';
        this.status = status;
        this.code = code;
        this.data = data;
    }
}

/** Bounded wait for POST /api/refresh — a hung refresh must fail, not stall the app forever. */
const REFRESH_TIMEOUT_MS = 15000;

/**
 * Should a 401 trigger the silent refresh-and-retry flow?
 *
 * Primary signal: the BE's machine code (`TOKEN_EXPIRED` / `TOKEN_INVALID`),
 * stamped by the API error handler for JWT-layer failures only. The
 * `/jwt/i` message sniff remains as a fallback for API builds that predate
 * the code — the BE flattens 401 messages to "Unauthorized", so the sniff
 * alone can never see a JWT problem (the bug that silently killed sessions).
 * Application-level 401s (wrong password, permission denials, handler bugs)
 * carry no token code and stay ineligible, preserving the 4/21 fix.
 */
export function isRefreshEligible401(status: number | undefined, data: unknown): boolean {
    if (status !== 401) return false;
    const body = data as {code?: unknown; error?: unknown; message?: unknown} | undefined;
    if (body?.code === 'TOKEN_EXPIRED' || body?.code === 'TOKEN_INVALID') return true;
    const message =
        (typeof body?.error === 'string' && body.error) || (typeof body?.message === 'string' && body.message) || '';
    return /jwt/i.test(message);
}

/**
 * Did /api/refresh fail in a way that means the session is truly dead?
 * 401/403 = cookie missing/expired/revoked → logging out is correct.
 * Network errors, timeouts, and 5xx are transient — keep the session so a
 * later attempt can still recover instead of dumping the user to /login.
 */
export function isDefinitiveRefreshFailure(error: unknown): boolean {
    const status = (error as {response?: {status?: unknown}} | undefined)?.response?.status;
    return status === 401 || status === 403;
}

/**
 * Base API client with centralized configuration and error handling
 */
class ApiClient {
    private client: AxiosInstance;
    private token: string | null = null;
    private sessionOnly = false;
    private onTokenUpdated: ((token: string) => void) | null = null;
    private isRefreshing = false;
    private failedQueue: Array<{resolve: (value?: any) => void; reject: (reason?: any) => void}> = [];
    private refreshTimer: ReturnType<typeof setTimeout> | null = null;
    private refreshInFlight: Promise<string> | null = null;

    constructor() {
        this.client = axios.create({
            baseURL: settings.apiURL,
            timeout: 30000, // 30 seconds
            withCredentials: true, // Enable cookies for refresh token
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Access tokens are kept in memory only.
        this.clearLegacyStoredTokens();
        this.setupInterceptors();
    }

    /**
     * Remove legacy tokens persisted by older builds.
     */
    private clearLegacyStoredTokens(): void {
        try {
            localStorage.removeItem('token');
            sessionStorage.removeItem('token');
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Get the static subdomain override from VITE_TENANT_SUBDOMAIN.
     * Only needed when running on localhost without a reverse proxy.
     * In all proxied environments (dev, staging, production) the browser Origin
     * header already carries the correct tenant subdomain.
     */
    private getSubdomainOverride(): string | null {
        const envSubdomain = import.meta.env.VITE_TENANT_SUBDOMAIN;
        return envSubdomain ? String(envSubdomain).trim() : null;
    }

    /**
     * Set up request and response interceptors
     */
    private setupInterceptors(): void {
        // Request interceptor to add auth token and tenant header
        this.client.interceptors.request.use(
            (config) => {
                // Add authorization token if available
                if (this.token) {
                    config.headers.Authorization = `Bearer ${this.token}`;
                }

                // Add tenant subdomain header for development/testing
                // This is only needed when running locally with subdomain override
                const subdomainOverride = this.getSubdomainOverride();
                if (subdomainOverride) {
                    config.headers['X-Tenant-Subdomain'] = subdomainOverride;
                }

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor for error handling and token refresh
        this.client.interceptors.response.use(
            (response: AxiosResponse) => {
                return response;
            },
            async (error: AxiosError) => {
                const originalRequest = error.config as any;

                // Distinguish JWT problems (refresh might help) from application-level
                // auth denials (refresh CAN'T help — wrong password, unverified user,
                // bug in a handler that 401s for the wrong reason).
                //
                // Old behavior treated every 401 as a JWT problem and triggered
                // refresh + retry. When the underlying issue wasn't token-related
                // (4/21 health-profile incident: handler 401'd because of a typo'd
                // claim key), the retry hit the same 401, the loop bailed, and the
                // user was bounced to /login looking like their account was broken.
                //
                // Current rule: refresh when the BE stamps a TOKEN_EXPIRED /
                // TOKEN_INVALID machine code (see api errorHandler.ts). The former
                // /jwt/i message sniff never matched the BE's flattened
                // "Unauthorized" body, which silently disabled this entire flow —
                // expired sessions just rotted with no refresh and no logout.
                const isJwtIssue = isRefreshEligible401(error.response?.status, error.response?.data);

                // Skip token refresh for auth endpoints (login, register, password reset, etc.)
                const authEndpoints = [
                    '/api/login',
                    '/api/register',
                    '/api/auth',
                    '/api/reset',
                    '/api/updatePass',
                    '/api/verify',
                    '/api/refresh', // Don't retry refresh endpoint itself
                    '/api/provider/patient-launch/exchange',
                    '/api/admin/org-switch/exchange',
                    '/api/org/provider-switch/exchange',
                    '/api/org/console-switch/exchange',
                ];
                const isAuthEndpoint = authEndpoints.some((endpoint) => originalRequest?.url?.includes(endpoint));

                // Handle JWT problems specifically — try refresh, then retry once.
                // Restrict to 401: express-jwt and the standard JWT middleware always
                // 401 on token problems. A 500 with "jwt" in the message would be a
                // backend bug, and the right response is to surface it, not to refresh
                // and pretend it's a token rotation. Refresh-on-500 was the loop that
                // bit us in the 4/21 incident.
                if (isJwtIssue && error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
                    if (this.sessionOnly) {
                        // Session-only impersonation tokens have no refresh path
                        // (patient-launch issues a 15-min JWT with no cookie).
                        // On expiry, fall back to the impersonator's admin
                        // session if we have it stashed in originalAdminData,
                        // show a toast so the user knows what happened, and
                        // navigate back to /admin.  Previously this branch
                        // silently rejected and the FE rendered the patient
                        // dashboard as empty — misleading, since the patient
                        // actually has data, the session just died.
                        toast.error('Impersonation session expired. Please re-impersonate to continue.', {
                            autoClose: 5000,
                        });
                        exitImpersonationDueToExpiry();
                        return Promise.reject(this.handleApiError(error));
                    }
                    if (this.isRefreshing) {
                        // Already refreshing — wait for the in-flight refresh to
                        // resolve, then retry once. Marking _retry=true prevents
                        // this queued request from spawning ITS own refresh if its
                        // retry happens to 401 too (which would burn a second
                        // rotation cycle for nothing).
                        originalRequest._retry = true;
                        return new Promise((resolve, reject) => {
                            this.failedQueue.push({resolve, reject});
                        }).then(() => this.client.request(originalRequest));
                    }

                    originalRequest._retry = true;
                    this.isRefreshing = true;

                    try {
                        const newToken = await this.refreshAccessToken();
                        this.processFailedQueue(null);

                        // Retry the original request with new token
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return this.client.request(originalRequest);
                    } catch (refreshError) {
                        // Refresh failed — every queued request and the originator
                        // get the SAME CustomApiError shape so callers don't have
                        // to special-case a bare Error mid-pipeline. Queued requests
                        // resolve back into the user's UI as standard 401s.
                        //
                        // Logout only when /api/refresh itself said the session is
                        // dead (401/403: cookie missing/expired/revoked). A network
                        // blip or 5xx during refresh is transient — dumping the
                        // user to /login for it would lose their in-progress work.
                        const apiError = this.handleApiError(error);
                        this.processFailedQueue(apiError);
                        if (isDefinitiveRefreshFailure(refreshError)) {
                            this.logoutUser();
                        }
                        return Promise.reject(apiError);
                    } finally {
                        this.isRefreshing = false;
                    }
                }

                return Promise.reject(this.handleApiError(error));
            }
        );
    }

    /**
     * Refresh the access token using the httpOnly refresh cookie.
     *
     * Single-flight: concurrent callers (boot effect under StrictMode's
     * double-invoke, the proactive timer, the interceptor, view-level
     * refreshTokenFromStorage) share ONE in-flight request. Two parallel
     * refreshes with the same cookie trip the BE's rotation replay
     * detection, which revokes the user's ENTIRE refresh-token family —
     * so coalescing here is a correctness fix, not an optimization.
     */
    private refreshAccessToken(): Promise<string> {
        if (this.sessionOnly) {
            return Promise.reject(new Error('Session-only auth cannot be refreshed'));
        }
        if (this.refreshInFlight) {
            return this.refreshInFlight;
        }
        this.refreshInFlight = this.performTokenRefresh().finally(() => {
            this.refreshInFlight = null;
        });
        return this.refreshInFlight;
    }

    private async performTokenRefresh(): Promise<string> {
        // Prepare headers with subdomain override if present
        const headers: Record<string, string> = {};
        const subdomainOverride = this.getSubdomainOverride();
        if (subdomainOverride) {
            headers['X-Tenant-Subdomain'] = subdomainOverride;
        }

        // Use raw axios here to avoid circular dependency (this is called from interceptor)
        const response = await axios.post(
            `${settings.apiURL}/api/refresh`,
            {},
            {
                withCredentials: true, // Send httpOnly refresh cookie
                headers,
                timeout: REFRESH_TIMEOUT_MS,
            }
        );

        const result = response.data.result || response.data;
        const newToken = result.token;

        if (!newToken) {
            throw new Error('No token returned from refresh endpoint');
        }

        // Update access token
        this.setToken(newToken);

        return newToken;
    }

    /**
     * Process the queue of failed requests after token refresh
     */
    private processFailedQueue(error: any): void {
        this.failedQueue.forEach(({resolve, reject}) => {
            if (error) {
                reject(error);
            } else {
                resolve(null);
            }
        });

        this.failedQueue = [];
    }

    /**
     * Logout user and clear all data
     */
    private logoutUser(): void {
        // Clear tokens and all client-side data
        this.clearTokens();

        try {
            // Clear all client-side data (security measure)
            localStorage.clear();
            sessionStorage.clear();

            // Clear all cookies
            document.cookie.split(';').forEach(function (c) {
                document.cookie = c
                    .replace(/^ +/, '')
                    .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
            });
        } catch {
            // Silently handle data clearing errors
        }

        // Redirect to login
        window.location.href = '/login';
    }

    /**
     * Handle API errors and convert them to CustomApiError
     */
    private handleApiError(error: AxiosError): CustomApiError {
        if (error.response) {
            // Server responded with error status
            const {status, data} = error.response;
            const message = (data as any)?.error || error.message || 'An error occurred';

            // Handle specific error cases
            switch (status) {
                case 401: {
                    // Only clear token if this is NOT an auth endpoint AND NOT a session-only
                    // (impersonation) session. In session-only mode the token cannot be refreshed,
                    // so clearing it would destroy the impersonation and redirect to login.
                    const authEndpoints = [
                        '/api/login',
                        '/api/register',
                        '/api/auth',
                        '/api/reset',
                        '/api/updatePass',
                        '/api/verify',
                        '/api/provider/patient-launch/exchange',
                        '/api/admin/org-switch/exchange',
                        '/api/org/provider-switch/exchange',
                        '/api/org/console-switch/exchange',
                    ];
                    const isAuthEndpoint = authEndpoints.some((endpoint) => error.config?.url?.includes(endpoint));

                    // Only clear the access token when the failure looks like a
                    // JWT problem (expired/malformed — the BE's TOKEN_* machine
                    // code, or the legacy jwt-message fallback). For
                    // application-level 401s (wrong password, unverified user,
                    // BE handler bug) the existing token is still valid; clearing
                    // it just makes the next request fail too.
                    const isJwtIssue = isRefreshEligible401(status, data);
                    if (!isAuthEndpoint && !this.sessionOnly && isJwtIssue) {
                        this.clearToken();
                    }

                    // Surface the BE's specific error string when present (e.g.
                    // "Incorrect password", "User not verified") so the UI can show
                    // something meaningful instead of a generic banner. Falls back
                    // to "Authentication required" only when the BE supplied nothing.
                    const surfacedMessage =
                        typeof (data as any)?.error === 'string' && (data as any).error
                            ? (data as any).error
                            : 'Authentication required';
                    return new CustomApiError(surfacedMessage, status, 'UNAUTHORIZED', data);
                }
                case 403:
                    return new CustomApiError('Access forbidden', status, 'FORBIDDEN', data);
                case 404:
                    return new CustomApiError('Resource not found', status, 'NOT_FOUND', data);
                case 422: {
                    const msg =
                        typeof (data as any)?.error === 'string' && (data as any).error
                            ? (data as any).error
                            : typeof (data as any)?.message === 'string' && (data as any).message
                              ? (data as any).message
                              : 'Validation failed';
                    return new CustomApiError(msg, status, 'VALIDATION_ERROR', data);
                }
                case 429:
                    return new CustomApiError('Too many requests', status, 'RATE_LIMITED', data);
                case 500:
                    return new CustomApiError('Server error', status, 'SERVER_ERROR', data);
                default:
                    return new CustomApiError(message, status, 'API_ERROR', data);
            }
        } else if (error.request) {
            // Network error
            return new CustomApiError('Network error - please check your connection', undefined, 'NETWORK_ERROR');
        } else {
            // Something else happened
            return new CustomApiError(error.message || 'An unexpected error occurred', undefined, 'UNKNOWN_ERROR');
        }
    }

    /**
     * Set authentication token (backward compatibility)
     */
    setToken(token: string): void {
        this.token = token;
        // Token persistence is disabled to reduce XSS exposure.
        if (this.sessionOnly) {
            if (this.refreshTimer) {
                clearTimeout(this.refreshTimer);
                this.refreshTimer = null;
            }
        } else {
            // Start proactive refresh timer
            this.startRefreshTimer();
        }

        // Notify auth context of token update
        if (this.onTokenUpdated) {
            this.onTokenUpdated(token);
        }
    }

    /**
     * Start proactive token refresh timer
     * Refreshes token 2 minutes before expiration (13 minutes for 15-minute tokens)
     */
    private startRefreshTimer(): void {
        // Clear existing timer
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }

        // Set timer for 13 minutes (refresh 2 minutes before 15-minute token expires)
        this.refreshTimer = setTimeout(
            async () => {
                try {
                    await this.refreshAccessToken();
                    // Restart timer for the new token
                    this.startRefreshTimer();
                } catch (error) {
                    logWarn('Proactive token refresh failed', {error});
                    // Don't logout here - let the user continue until they make a request that fails
                    // The interceptor will handle the refresh at that point
                }
            },
            13 * 60 * 1000
        ); // 13 minutes in milliseconds
    }

    /**
     * Set callback for token updates (used by auth context)
     */
    setTokenUpdatedCallback(callback: (token: string) => void): void {
        this.onTokenUpdated = callback;
    }

    /**
     * Clear authentication token (backward compatibility)
     */
    clearToken(): void {
        this.token = null;

        // Clear refresh timer
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }

        // Remove legacy stored tokens if present.
        try {
            localStorage.removeItem('token');
            sessionStorage.removeItem('token');
        } catch {
            // Silently handle storage removal errors
        }

        // Notify auth context of token removal
        if (this.onTokenUpdated) {
            this.onTokenUpdated('');
        }
    }

    /**
     * Clear tokens and cleanup
     */
    clearTokens(): void {
        this.token = null;

        // Clear refresh timer
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }

        // Remove legacy stored tokens if present.
        try {
            localStorage.removeItem('token');
            sessionStorage.removeItem('token');
        } catch {
            // Silently handle storage removal errors
        }

        // Notify auth context of token removal
        if (this.onTokenUpdated) {
            this.onTokenUpdated('');
        }
    }

    /**
     * Refresh token from httpOnly cookie (memory-only access tokens).
     */
    refreshTokenFromStorage(): void {
        void this.refreshAccessToken().catch(() => undefined);
    }

    /**
     * Token persistence has been removed; method is retained for compatibility.
     */
    setSessionStorageMode(useSessionStorage: boolean): void {
        this.sessionOnly = useSessionStorage;
        if (useSessionStorage && this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    /**
     * Get current token
     */
    getToken(): string | null {
        return this.token;
    }

    /**
     * Manually trigger token refresh (useful for testing or manual refresh)
     */
    async manualRefresh(): Promise<string | null> {
        try {
            return await this.refreshAccessToken();
        } catch (error) {
            logError('Manual token refresh failed', error);
            return null;
        }
    }

    /**
     * Generic GET request
     */
    async get<T>(url: string, config?: any): Promise<T> {
        const response = await this.client.get<T>(url, config);
        return response.data;
    }

    /**
     * Generic POST request
     */
    async post<T>(url: string, data?: any, config?: any): Promise<T> {
        const response = await this.client.post<T>(url, data, config);
        return response.data;
    }

    /**
     * Generic PUT request
     */
    async put<T>(url: string, data?: any, config?: any): Promise<T> {
        const response = await this.client.put<T>(url, data, config);
        return response.data;
    }

    /**
     * Generic PATCH request
     */
    async patch<T>(url: string, data?: any, config?: any): Promise<T> {
        const response = await this.client.patch<T>(url, data, config);
        return response.data;
    }

    /**
     * Generic DELETE request
     */
    async delete<T>(url: string, config?: any): Promise<T> {
        const response = await this.client.delete<T>(url, config);
        return response.data;
    }

    /**
     * Upload file with progress tracking
     */
    async uploadFile<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
        const formData = new FormData();
        formData.append('file', file);

        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent: any) => {
                if (onProgress && progressEvent.total) {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(progress);
                }
            },
        };

        const response = await this.client.post<T>(url, formData, config);
        return response.data;
    }
}

// Create and export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
